import Image from 'next/image';

import { Badge, Container, Link, NextLink, Text } from '@/atoms';

import { formatDateNumeric, getUrlExtension, isPNG } from '@/lib/utils';
import FallBackIMG from '@/assets/images/fallback-product-card.jpg';

import type { FunctionComponent } from 'react';
import type { TypeCardBlogProps } from './types';

/**
 * CardBlog
 */
export const CardBlog: FunctionComponent<TypeCardBlogProps> = ({
  title,
  excerpt,
  date,
  readingTime,
  href,
  image,
  badge,
  className,
  asTitle = 'h3',
}: TypeCardBlogProps): JSX.Element => {
  const srcWithFallback = image?.src || FallBackIMG.src;
  const objectFit = isPNG(getUrlExtension(srcWithFallback))
    ? 'object-contain'
    : 'object-cover';

  return (
    <Container
      className={`h-full w-full gap-4 outline outline-1 outline-primary-200 ${className}`}
      padding="p-2"
      backgroundColor="bg-bg-secondary"
    >
      <NextLink href={href}>
        <a className="relative h-[166px] w-full">
          <Image
            src={srcWithFallback}
            alt={image?.alt || title}
            fill
            draggable="false"
            className={`object-center ${objectFit}`}
            sizes="(max-width: 360px) 350px, (max-width: 450px) 450px, (max-width: 600px) 300px, (max-width: 800px) 370px"
          />

          {badge && (
            <Badge
              fullRounded
              style="orange"
              className="absolute right-1 top-1"
            >
              {badge}
            </Badge>
          )}
        </a>
      </NextLink>

      <div className="flex grow flex-col gap-4 p-1">
        <div className="u-label u-label--overlline flex flex-row justify-between text-primary-400">
          <p className="self-start">{formatDateNumeric(date)}</p>
          {readingTime && <p className="self-end">{readingTime} MIN</p>}
        </div>

        <div className="flex grow flex-col gap-1 text-primary-900">
          <Text as={asTitle} className="u-headline u-headline--h4 line-clamp-2">
            <NextLink href={href}>
              <a>{title}</a>
            </NextLink>
          </Text>

          {excerpt && (
            <div
              className="u-body u-body--xs line-clamp-2"
              {...(typeof excerpt === 'string'
                ? { dangerouslySetInnerHTML: { __html: excerpt } }
                : { children: excerpt })}
            />
          )}
        </div>

        <NextLink href={href} passHref>
          <Link size="small" type="primary">
            leer más
          </Link>
        </NextLink>
      </div>
    </Container>
  );
};

CardBlog.displayName = 'CardBlog';
